/**
 * ShapeResizeHandles Component
 *
 * Renders draggable resize handles at shape corners/vertices.
 * Handle visibility during operations:
 * - RESIZE: Only the dragged handle is visible (others hidden)
 * - DRAG: All handles hidden during shape drag
 * - IDLE: All handles visible
 *
 * Features:
 * - Draggable markers at corners (Rectangle), radius (Circle), vertices (Triangle)
 * - Smart visibility control during drag/resize
 * - Real-time shape update while dragging
 * - Minimum size validation (prevents tiny shapes)
 * - Z-index above shape to ensure clickability
 *
 * @module components/map/GeometryRenderer/ShapeRenderer/ShapeResizeHandles
 */

import React, { memo, useCallback } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { useShapeResize } from '../../../../hooks/useShapeResize';
import { useGeoMarkupStore } from '../../../../store';
import type { TaskGeometry, Coordinates } from '../../../../types';
import classes from '../LineRenderer/LinePointHandles.module.css'; // Shared handle styles

/**
 * Props for ShapeResizeHandles component
 */
export interface ShapeResizeHandlesProps {
  /** The shape geometry containing points to render */
  geometry: TaskGeometry;
  /** Whether drag mode is enabled */
  isDraggable: boolean;
}

/**
 * ShapeResizeHandles Component
 *
 * Renders interactive draggable handles at shape corners/vertices
 * Handles are always visible for better discoverability
 */
export const ShapeResizeHandles = memo(function ShapeResizeHandles({
  geometry,
  isDraggable,
}: ShapeResizeHandlesProps) {
  const shapePoints = geometry.shapePoints || [];

  // Hook for handling resize (includes draggedHandleIndexRef for visibility control)
  const { handleResizeStart, handleResize, handleResizeEnd, draggedHandleIndexRef } = useShapeResize(geometry.id);

  // Track if THIS geometry is being dragged or resized
  const draggingGeometryId = useGeoMarkupStore((s) => s.draggingGeometryId);
  const isDraggingOrResizing = draggingGeometryId === geometry.id;

  // Prevent click from bubbling to map (stops unwanted shape creation)
  // NOTE: No onMouseDown handler - it would block MapLibre Marker drag initialization
  // onClick is sufficient because it only fires if NO drag occurred (browser standard)
  const handleHandleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from reaching map click handler
    e.preventDefault(); // Extra safety
  }, []);

  // Don't render if no points or not draggable
  if (shapePoints.length === 0 || !isDraggable) {
    return null;
  }

  return (
    <>
      {shapePoints.map((point: Coordinates, index: number) => {
        // Visibility logic:
        // - RESIZE: Show only the dragged handle (draggedHandleIndexRef.current === index)
        // - DRAG: Hide all handles (isDraggingOrResizing && draggedHandleIndexRef.current === null)
        // - IDLE: Show all handles (!isDraggingOrResizing)
        const isThisHandleBeingDragged = isDraggingOrResizing && draggedHandleIndexRef.current === index;
        const shouldShowHandle = !isDraggingOrResizing || isThisHandleBeingDragged;

        return (
          <Marker
            key={`${geometry.id}-handle-${index}`}
            latitude={point.lat}
            longitude={point.lng}
            draggable={isDraggable}
            onDragStart={(e) => handleResizeStart(e, index)}
            onDrag={(e) => handleResize(e, index)}
            onDragEnd={(e) => handleResizeEnd(e, index)}
          >
            {/* Smart visibility: show only active handle during resize, hide all during drag */}
            {/* Stop click propagation to prevent unwanted shape creation (no onMouseDown - would block drag) */}
            <div
              className={`${classes.shapeResizeHandle} ${shouldShowHandle ? classes.visible : classes.hidden}`}
              title="Resize handle"
              onClick={handleHandleClick}
              data-resize-handle-index={index}
            >
              {/* Visual handle (circle) */}
              <div className={classes.handleInner} />
            </div>
          </Marker>
        );
      })}
    </>
  );
});
